import React, { ReactElement, useEffect, useState } from 'react'
import { Alert, Col, Placeholder, Row } from 'react-bootstrap'
import { useRouter } from 'next/router'

import Alerts, { AlertProps } from '@components/alerts'
import Title from '@components/text/title'
import API_Client from '@services/api-client'
import { FlowProcess } from '@services/flow-client'
import { MINUTES_IN_MILLISECONDS } from '@utils/constants'
import { runCallbackIntoIntervall } from '@utils/misc'
import { countIf } from '@utils/objects'
import { EqualHeight, EqualHeightElement } from 'react-equal-height'
import { ArrowPathIcon } from '@heroicons/react/24/outline'

export default function Activities(): ReactElement {
  const router = useRouter();

  const [loaded, setLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [alerts, setAlerts] = useState<AlertProps[]>([]);

  const [inProgressTotal, setInProgressTotal] = useState(0);
  const [onAlertTotal, setOnAlertTotal] = useState(0);
  const [delayedTotal, setDelayedTotal] = useState(0);
  const [colsSize, setColsSize] = useState(12);

  const loadActivies = async () => {
    const response = await API_Client().get('flow/pending-processes');

    setLoaded(true);
    if (! response.success) {
      setHasError(true);
      return;
    }

    const activities = response.data as FlowProcess[];

    const inProgress = activities.filter(p => p.status === "IN_PROGRESS").length;
    setInProgressTotal(inProgress);

    const onAlert = activities.filter(p => p.status === "ON_ALERT").length;
    setOnAlertTotal(onAlert);

    const delayed = activities.filter(p => p.status === "DELAYED").length;
    setDelayedTotal(delayed);

    // Prepare alerts
    const featured: AlertProps[] = [];
    for (let i = 0; i < activities.length; i++) {
      if (featured.length >= 3) {
        break;
      }

      const process: FlowProcess = activities[i];
      const message = (process.activityTitle === process.processTitle) ? `<span class="process-title">${process.processTitle}</span>` : `<span class="process-title">${process.processTitle}</span> - ${process.activityTitle}`;
      featured.push({
        key: `alert-${process.processInstanceId}`,
        message: message,
        type: 'gray',
        link: process.url,
      } as AlertProps);

      setAlerts(featured);
    }
  };

  useEffect(() => {
    const interval = 5 * MINUTES_IN_MILLISECONDS;
    return runCallbackIntoIntervall(loadActivies, interval);
  }, []);

  useEffect(() => {
    const cols = countIf([
      inProgressTotal > 0,
      onAlertTotal > 0,
      delayedTotal > 0,
    ]);

    setColsSize(12 / cols);
  }, [inProgressTotal, onAlertTotal, delayedTotal]);

  const onClickUpdateActivities = async () => {
    setIsLoading(true);
    await loadActivies();
    setIsLoading(false);
  }

  const reloadPage = (event) => {
    event.preventDefault();
    router.reload();
  };

  if (hasError) {
    return (
      <>
        <Title as="h3">Flow</Title>
        <EqualHeight>
          <Row className="activities-wrapper">
            <Col xs="12" md="12" lg="5">
              <Row>
                <p>Tivemos um problema ao carregar os processos.</p>
                <p>Por favor, tente <a className="regular-link" onClick={ onClickUpdateActivities }>atualizar</a> ou <a className="regular-link" onClick={ reloadPage }>recarregar a página</a>.</p>
              </Row>
            </Col>
          </Row>
        </EqualHeight>
      </>
    );
  }

  if (isLoading || ! loaded) {
    return (
      <div className="activities-component-wrapper">
        <Title as="h3" className="section-title">
          Flow
          <span className="btn-update animated"><ArrowPathIcon /></span>
        </Title>
        <Row className="activities-wrapper">
          <Col xs="12" md="12" lg="5">
            <Row>
              <Col>
                <div className="activity activity-placeholder">
                  <Placeholder as={'h3'} xs={4}>
                    <Placeholder />
                  </Placeholder>
                  <Placeholder as={'span'} xs={12} className="activity-qtd">
                    <Placeholder />
                  </Placeholder>
                </div>
              </Col>
              <Col>
                <div className="activity activity-placeholder">
                  <Placeholder as={'h3'} xs={4}>
                    <Placeholder />
                  </Placeholder>
                  <Placeholder as={'span'} xs={12} className="activity-qtd">
                    <Placeholder />
                  </Placeholder>
                </div>
              </Col>
              <Col>
                <div className="activity activity-placeholder">
                  <Placeholder as={'h3'} xs={4}>
                    <Placeholder />
                  </Placeholder>
                  <Placeholder as={'span'} xs={12} className="activity-qtd">
                    <Placeholder />
                  </Placeholder>
                </div>
              </Col>
            </Row>
          </Col>
          <Col xs="12" md="12" lg="7">
            <Alerts alerts={[]} loaded={ false }/>
            <Alerts alerts={[]} loaded={ false }/>
          </Col>
        </Row>
      </div>
    );
  }

  const hasActivities = inProgressTotal + onAlertTotal + delayedTotal;

  if (! hasActivities) {
    return (
      <div className="activities-component-wrapper">
        <Title as="h3" className="section-title">
          Flow
          <a onClick={ onClickUpdateActivities } className="btn-update"><ArrowPathIcon /></a>
        </Title>
        <Row className="activities-wrapper">
          <Col xs="12">
            <Alert variant="success">
              <div className="d-flex flex-grow-1">
                <div className="text-start alert-content-wrapper">
                  <p className="alert-content mb-0"><strong className="pe-2">Parabéns!</strong> Nenhuma atividade pendente no Flow.</p>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>
      </div>
    )
  }

  return (
    <div className="activities-component-wrapper">
      <Title as="h3" className="section-title">
        Flow
        <a onClick={ onClickUpdateActivities } className="btn-update"><ArrowPathIcon /></a>
      </Title>
      <EqualHeight>
        <Row className="activities-wrapper">
          <Col xs="12" md="12" lg="5">
            <Row>
              { inProgressTotal > 0 && (
                <Col xs="12" md={colsSize}>
                  <div className="activity activity-in-progress mb-3">
                    <EqualHeightElement name="activity-height">
                      <h3 className="activity-label">No prazo</h3>
                      <span className="activity-qtd">{inProgressTotal}</span>
                    </EqualHeightElement>
                  </div>
                </Col>
              ) }
              { onAlertTotal > 0 && (
                <Col xs="12" md={colsSize}>
                  <div className="activity activity-on-alert mb-3">
                    <EqualHeightElement name="activity-height">
                      <h3 className="activity-label">Em alerta</h3>
                      <span className="activity-qtd">{onAlertTotal}</span>
                    </EqualHeightElement>
                  </div>
                </Col>
              ) }
              { delayedTotal > 0 && (
                <Col xs="12" md={colsSize}>
                  <div className="activity activity-delayed mb-3">
                    <EqualHeightElement name="activity-height">
                      <h3 className="activity-label">Atrasado</h3>
                      <span className="activity-qtd">{delayedTotal}</span>
                    </EqualHeightElement>
                  </div>
                </Col>
              ) }
            </Row>
          </Col>
          <Col xs="12" md="12" lg="7" className="mb-3">
            <EqualHeightElement name="activity-height">
              <div className="activities-list">
                <Alerts alerts={alerts} loaded={ loaded }/>
              </div>
            </EqualHeightElement>
          </Col>
        </Row>
      </EqualHeight>
    </div>
  );
}
